// src/components/auth/PermissionDeniedRedirect.tsx
//
// Fallback for inner route gates (engineering / buyer / admin / etc.) — the
// outer AppLayout READ gate has already passed, so the dashboard "/" is safe
// to bounce to. Before redirecting we surface a permission-denied toast so the
// user understands WHY they were redirected — silent redirects to "/" are a
// poor enterprise UX.
//
// Usage:
//   <ProtectedRoute
//     requiredPermissions={[CATALOG_PERMISSIONS.REVIEW_ENGINEER]}
//     fallback={<PermissionDeniedRedirect feature="Engineering Review" />}
//   >
//     <Outlet />
//   </ProtectedRoute>

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyPermissionDenied } from '../../utils/notifications';

interface Props {
  /** Human-readable feature name shown in the toast (e.g. "Engineering Review"). */
  feature: string;
  /** Permission codes required to enter this route. Surfaced in the toast body. */
  requiredPermissions: string[];
  /** AND vs OR semantics across the required codes. Default 'all'. */
  mode?: 'all' | 'any';
  /** Where to send the user. Defaults to the dashboard. */
  redirectTo?: string;
}

export const PermissionDeniedRedirect = ({
  feature,
  requiredPermissions,
  mode = 'all',
  redirectTo = '/',
}: Props) => {
  const navigate = useNavigate();

  useEffect(() => {
    notifyPermissionDenied({
      action: `open ${feature}`,
      requiredPermissions,
      mode,
      key: `route-denied:${feature}`,
    });
    navigate(redirectTo, { replace: true });
  }, [feature, requiredPermissions, mode, redirectTo, navigate]);

  return null;
};
